import isObject from 'lodash/isObject';
import isEmpty from 'lodash/isEmpty';
import { LanguageCodeEnums, ConversationReplyTypeEnum, TranslatableLanguageCodes } from './enums';
import { notEmpty } from './form-validation-utils';
import { SystemTextEnum, getIsSystemText } from './i18n';

const {
	ZH_CN, TH, VI, EN, BN, HI, JA, KO, PT, RU, ZH_TW, AUTO,
} = LanguageCodeEnums;
const {
	TEXT,
	GREETING,
	FORM_TAGS,
	ASSISTANT_TEXT,
} = ConversationReplyTypeEnum;
const { FORM_PLACEHOLDER } = SystemTextEnum;

export function getTranslateKey(id, languageCode) {
	return `${id}_${languageCode}`;
}

export const getKeyValueArrayForTranslation = (userProfile = {}) => {
	const userProfileArr = [];

	for (const [key, val] of Object.entries(userProfile)) {
		userProfileArr.push(key);

		if (val === '') {
			userProfileArr.push(' ');
			continue;
		}

		userProfileArr.push(`${val}`);
	}

	return userProfileArr;
};

export const LanguageCodeMap = {
	// 簡體中文
	[ZH_CN]: ZH_CN,
	'zh-hans': ZH_CN,

	// 繁體中文
	[ZH_TW]: ZH_TW,
	'zh-hant': ZH_TW,
	'zh-hk': ZH_TW,
	'zh-mo': ZH_TW,

	// 英文
	[EN]: EN,
	'en-us': EN,
	'en-gb': EN,
	'en-au': EN,
	'en-ca': EN,
	'en-in': EN,

	// 越南文
	[VI]: VI,

	// 泰文
	[TH]: TH,

	// 孟加拉文
	[BN]: BN,
	'bn-bd': BN,
	'bn-in': BN,

	// 印地文
	[HI]: HI,

	// 日文
	[JA]: JA,

	// 韓文
	[KO]: KO,
	'ko-kr': KO,

	// 葡萄牙文
	[PT]: PT,
	'pt-br': PT,
	'pt-pt': PT,

	// 俄文
	[RU]: RU,
	'ru-ru': RU,
};

// 用戶端點「原文」一律顯示英文譯文，避免會員從客服原始語言推測其所在國家／地區
const ORIGINAL_IN_ENGLISH = {
	btnName: '原文',
	toTranslateCode: EN,
};

export const clientTranslationSetting = {
	[ZH_CN]: {
		isTranslatable: false,
	},
	[TH]: {
		isTranslatable: true,
		original: ORIGINAL_IN_ENGLISH,
		translation: {
			btnName: '泰语',
			toTranslateCode: TH,
		},
	},
	[VI]: {
		isTranslatable: true,
		original: ORIGINAL_IN_ENGLISH,
		translation: {
			btnName: '越南语',
			toTranslateCode: VI,
		},
	},
	[EN]: {
		isTranslatable: true,
		original: ORIGINAL_IN_ENGLISH,
		translation: {
			btnName: '英文',
			toTranslateCode: EN,
		},
	},
	[BN]: {
		isTranslatable: true,
		original: ORIGINAL_IN_ENGLISH,
		translation: {
			btnName: '孟加拉语',
			toTranslateCode: BN,
		},
	},
	[HI]: {
		isTranslatable: true,
		original: ORIGINAL_IN_ENGLISH,
		translation: {
			btnName: '印地语',
			toTranslateCode: HI,
		},
	},
	[JA]: {
		isTranslatable: true,
		original: ORIGINAL_IN_ENGLISH,
		translation: {
			btnName: '日语',
			toTranslateCode: JA,
		},
	},
	[KO]: {
		isTranslatable: true,
		original: ORIGINAL_IN_ENGLISH,
		translation: {
			btnName: '韩语',
			toTranslateCode: KO,
		},
	},
	[PT]: {
		isTranslatable: true,
		original: ORIGINAL_IN_ENGLISH,
		translation: {
			btnName: '葡萄牙语',
			toTranslateCode: PT,
		},
	},
	[RU]: {
		isTranslatable: true,
		original: ORIGINAL_IN_ENGLISH,
		translation: {
			btnName: '俄语',
			toTranslateCode: RU,
		},
	},
	[ZH_TW]: {
		isTranslatable: true,
		original: ORIGINAL_IN_ENGLISH,
		translation: {
			btnName: '繁体中文',
			toTranslateCode: ZH_TW,
		},
	},
};
export const DEFAULT_CLIENT_TRANSLATION_SETTING = {
	isTranslatable: true,
	original: ORIGINAL_IN_ENGLISH,
	translation: {
		btnName: '翻译',
		toTranslateCode: EN,
	},
};

const ZH_CN_AUTO_TRANSLATION_SETTING = {
	isTranslatable: true,
	original: {
		btnName: '原文',
		toTranslateCode: null,
	},
	translation: {
		btnName: '简体中文',
		toTranslateCode: ZH_CN,
	},
};

// Resolves the translation UI config for a client, taking into account the
// special ZH_CN + auto-detect platform case which overrides the static
// `clientTranslationSetting[ZH_CN]` entry.
export const resolveClientTranslationSetting = (meLanguageCode, platformLanguageCode) => {
	if (platformLanguageCode === AUTO && meLanguageCode === ZH_CN) {
		return ZH_CN_AUTO_TRANSLATION_SETTING;
	}

	return clientTranslationSetting[meLanguageCode] || {};
};

// 語言正規化
export const getNormalizedLanguage = (lang = '') => LanguageCodeMap[lang.toLowerCase()] || EN;

const TranslateConversationReplyTypeEnum = {
	[TEXT]: TEXT,
	[GREETING]: GREETING,
	[FORM_TAGS]: FORM_TAGS,
};

export const getIsTranslateConversationReplyType = type => Boolean(TranslateConversationReplyTypeEnum[type]);

const FALLBACK_LANGUAGE_CODE = ZH_CN;

export const getTranslatableText = (translations = {}, languageCode) => {
	if (!isObject(translations)) {
		return translations;
	}

	if (notEmpty(translations[languageCode])) {
		return translations[languageCode];
	}

	if (notEmpty(translations[FALLBACK_LANGUAGE_CODE])) {
		return translations[FALLBACK_LANGUAGE_CODE];
	}

	const firstFilledCode = TranslatableLanguageCodes.find(code => notEmpty(translations[code]));

	return firstFilledCode ? translations[firstFilledCode] : '';
};

export const getLocalizedName = (name, language, platformLanguage) => {
	if (!isObject(name)) {
		return name;
	}

	return notEmpty(name[language]) ? name[language] : getTranslatableText(name, platformLanguage);
};

// 物件資料不做語系間回填：會讓未填語系在編輯彈窗顯示成已填
export const normalizeTranslations = content => {
	const isLegacyText = !isObject(content);
	const fallbackText = getTranslatableText(content, FALLBACK_LANGUAGE_CODE);

	if (!notEmpty(fallbackText)) {
		return {};
	}

	return TranslatableLanguageCodes.reduce((acc, code) => {
		acc[code] = isLegacyText ? fallbackText : (content[code] || '');

		return acc;
	}, {});
};

// 兩支顯示解析，差別在 message 與 i18nMessage[語系] 同時有值時誰優先：
//   - resolvePreTranslatedText  → message 原文優先（i18nMessage 只在 message 為空的預翻譯訊息如 greeting 才用）
//   - resolveLocalizedReplyText → i18nMessage[語系] 譯文優先，缺語系才 fallback message 原文
// 對話 / 歷史 / 引用要「跟著語系顯示譯文」用後者；client 主訊息（use-node-translation、qa-ending）原文優先用前者。
export const getIsPreTranslatedMessage = (message, i18nMessage) => isEmpty(message) && !isEmpty(i18nMessage);

export const resolvePreTranslatedText = (message, i18nMessage, languageCode) =>
	getIsPreTranslatedMessage(message, i18nMessage) ? getTranslatableText(i18nMessage, languageCode) : message;

export const resolveLocalizedReplyText = (message, i18nMessage, languageCode) => {
	const i18nText = isObject(i18nMessage) ? i18nMessage[languageCode] : undefined;

	if (notEmpty(i18nText)) {
		return i18nText;
	}

	return notEmpty(message) ?
		message :
		getTranslatableText(i18nMessage, languageCode);
};

// AI 回覆的「思考過程」：i18nAssistantReason，轉成當前語系字串。
export const resolveAssistantReason = (conversationReply = {}, languageCode) => {
	const { type, i18nAssistantReason } = conversationReply;

	if (type !== ASSISTANT_TEXT) {
		return '';
	}

	if (isEmpty(i18nAssistantReason)) {
		return '';
	}

	return getTranslatableText(i18nAssistantReason, languageCode);
};

export const FIXED_FORM_MESSAGE = ['title', 'placeHolder', 'values'];
export const FIXED_FORM_CATEGORY = ['description', 'link', 'options'];

export const getFixedArrayKey = (fixedArray, objItem, name) => {
	return fixedArray.flatMap(key => {
		const value = objItem[key];

		if (!value || (Array.isArray(value) && value.length === 0)) {
			return [];
		}

		if (Array.isArray(value)) {
			return value.map((message, idx) => ({
				translateKey: `${name}.${key}.${idx}`,
				message,
			}));
		}

		return { translateKey: `${name}.${key}`, message: value };
	});
};

export const getTranslatedByMap = (translationMap, key, value) => translationMap?.[key] || value;

export const getLocalizedPlaceHolder = ({ translationMap, name, placeHolder, t }) => {
	const localizedPlaceHolder = getIsSystemText(FORM_PLACEHOLDER, placeHolder) ? t(FORM_PLACEHOLDER) : placeHolder;

	return getTranslatedByMap(translationMap, `${name}.placeHolder`, localizedPlaceHolder);
};

export const IMPORTANT_FIELDS_KEY = 'importantFields';

const MEMBER_TYPE_KEY = '会员类型';

// 值不可翻欄位：重組時 key 仍為譯文、value 以原值覆蓋。做成清單，方便未來擴充。
export const NON_TRANSLATABLE_VALUE_FIELDS = [
	'会员帐号',
	'memberName',
];

export const getTranslatedUserProfile = (userProfileArr = [], originalProfileArr = []) => {
	const userProfile = {};

	if (!Array.isArray(userProfileArr) || userProfileArr.length === 0) {
		return {};
	}

	const originalPairs = Array.isArray(originalProfileArr) ? originalProfileArr : [];

	for (let i = 0; i < userProfileArr.length; i += 2) {
		const originalKey = originalPairs[i];
		const isNonTranslatableValue = NON_TRANSLATABLE_VALUE_FIELDS.includes(originalKey);

		userProfile[userProfileArr[i]] = isNonTranslatableValue ? originalPairs[i + 1] : userProfileArr[i + 1];
	}

	return userProfile;
};

// 依重要欄位把 [key, value, ...] 交錯陣列拆成 [normalPairs, importantPairs] 兩組（皆維持交錯格式）
export function getKeyValueArrayByImportantFields(arr = [], importantFields = []) {
	if (!Array.isArray(arr) || arr.length === 0) {
		return [[], []];
	}

	const hasImportantFields = Array.isArray(importantFields) && importantFields.length > 0;

	if (!hasImportantFields) {
		return [arr.slice(), []];
	}

	const importantSet = new Set(importantFields);
	const normalPairs = [];
	const importantMap = new Map();

	for (let i = 0; i < arr.length; i += 2) {
		const key = arr[i];
		const value = arr[i + 1];

		if (importantSet.has(key)) {
			importantMap.set(key, value);
		} else {
			normalPairs.push(key, value);
		}
	}

	const sortedImportantPairs = [];

	for (const field of importantFields) {
		if (importantMap.has(field)) {
			sortedImportantPairs.push(field, importantMap.get(field));
		}
	}

	return [normalPairs, sortedImportantPairs];
}

// 由 userProfile 拆出個人資訊（normal）與重要資訊（important）兩組待翻譯配對
export function getInfoTranslatePairs(userProfile = {}) {
	const { [IMPORTANT_FIELDS_KEY]: importantFields, ...restUserProfile } = userProfile;

	return getKeyValueArrayByImportantFields(
		getKeyValueArrayForTranslation(restUserProfile),
		importantFields,
	);
}

// 翻譯 API 的輸入／輸出是 key、value 交錯的同序陣列（[key, value, ...]）；
function getTranslatedValueByOriginalKey(originalPairs = [], translatedArray = [], key) {
	if (!Array.isArray(originalPairs) || !Array.isArray(translatedArray)) {
		return undefined;
	}

	for (let i = 0; i < originalPairs.length; i += 2) {
		if (originalPairs[i] === key) {
			return translatedArray[i + 1];
		}
	}

	return undefined;
}

// TranslatedPairs：[[originalPairs, translatedArray], ...] 候選清單，支援 normal/important 雙 key 或單 key。
export function getTranslatedUserType({ userProfile = {}, isInfoTranslated, translatedPairs = [] }) {
	const rawUserType = userProfile?.[MEMBER_TYPE_KEY];

	if (!isInfoTranslated) {
		return rawUserType;
	}

	for (const [originalPairs, translatedArray] of translatedPairs) {
		const translated = getTranslatedValueByOriginalKey(originalPairs, translatedArray, MEMBER_TYPE_KEY);

		if (translated !== undefined) {
			return translated;
		}
	}

	return rawUserType;
}

export function pickImportantFields(obj = {}, importantFields = []) {
	return Object.fromEntries(
		importantFields.filter(key => key in obj).map(key => [key, obj[key]]),
	);
}

export const getIsTranslationFailed = (conversationReply = {}) =>
	Boolean(conversationReply.i18nMessageTranslationFailedAt);

// 對方發話改非同步翻譯：create 推原文、update 補譯文。
const TRANSLATING_TIMEOUT_MS = 15000;

// 無效 createdAt 回 0
export function getTranslateWindowRemaining(createdAt) {
	const createdTime = new Date(createdAt).getTime();

	if (!Number.isFinite(createdTime)) {
		return 0;
	}

	return Math.max(0, TRANSLATING_TIMEOUT_MS - (Date.now() - createdTime));
}

const getIsBackendAsyncTranslate = ({
	platformLanguage,
	isHistory,
	isAppendToRightSide,
} = {}) =>
	platformLanguage === AUTO && !isAppendToRightSide && !isHistory;
const getIsTranslatableReply = ({
	isShowTranslation,
	isPreTranslatedMessage,
	hasLocalizedReply,
	isHistory,
	isAppendToRightSide,
} = {}) => {
	const showLocalizedReplyOnly = hasLocalizedReply && !isAppendToRightSide && isHistory;

	return isShowTranslation && !isPreTranslatedMessage && !showLocalizedReplyOnly;
};

export const getReplyTranslationState = ({
	rawMessage,
	i18nMessage,
	language,
	platformLanguage,
	isShowTranslation,
	isAutoTranslate,
	isHistory,
	isAppendToRightSide,
	isTranslationFailed,
	hasTimedOut,
} = {}) => {
	const isPreTranslatedMessage = getIsPreTranslatedMessage(rawMessage, i18nMessage);
	const hasLocalizedReply = notEmpty(i18nMessage?.[language]);
	const isBackendAsyncTranslate = getIsBackendAsyncTranslate({ platformLanguage, isHistory, isAppendToRightSide });
	const isTranslatable = !isTranslationFailed && getIsTranslatableReply({
		isShowTranslation, isPreTranslatedMessage, hasLocalizedReply, isHistory, isAppendToRightSide,
	});
	const isTranslatePending = isBackendAsyncTranslate && isTranslatable && !hasLocalizedReply && !hasTimedOut;
	// 補譯交給後端，前端不重複翻
	const shouldAutoTranslate = isTranslatable && isAutoTranslate && (!isAppendToRightSide || isHistory) && !isBackendAsyncTranslate;
	const originalMessage = isTranslatable ? rawMessage : resolveLocalizedReplyText(rawMessage, i18nMessage, language);
	const localizedTranslation = hasLocalizedReply ? i18nMessage[language] : undefined;

	return {
		hasLocalizedReply,
		isTranslatable,
		isTranslatePending,
		isBackendAsyncTranslate,
		shouldAutoTranslate,
		originalMessage,
		localizedTranslation,
	};
};
